import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  jsonLd?: any | any[];
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (el) {
    el.setAttribute("content", content);
  } else {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("content", content);
    document.head.appendChild(el);
  }
}

export function useSEO({
  title,
  description,
  canonicalUrl,
  ogImage,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    document.title = title;

    // Update meta description
    upsertMeta("name", "description", description);

    // Update canonical URL
    if (canonicalUrl) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) {
        canonicalLink.setAttribute("href", canonicalUrl);
      } else {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        canonicalLink.setAttribute("href", canonicalUrl);
        document.head.appendChild(canonicalLink);
      }
    }

    // Open Graph / Twitter tags (per-page)
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    if (canonicalUrl) upsertMeta("property", "og:url", canonicalUrl);
    if (ogImage) {
      upsertMeta("property", "og:image", ogImage);
      upsertMeta("name", "twitter:image", ogImage);
    }
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);

    // JSON-LD structured data is now emitted by each route's `head()` during
    // SSR/prerender, so it is present in the initial HTML. Do not re-inject it
    // client-side here — that would create duplicate script tags.
    void jsonLd;
  }, [title, description, canonicalUrl, ogImage, jsonLd]);
}
